
import { format } from "date-fns";
import { Fragment, useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { 
  Phone, 
  Mail, 
  Calendar, 
  MessageSquare, 
  Clock, 
  CheckSquare,
  Pencil,
  User,
  Trash2
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { useToast } from "@/hooks/use-toast";

interface EventTimelineProps {
  events: any[];
  refreshKey?: number;
  leadId?: string;
}

// Define getEventIcon outside components so it can be shared
const getEventIcon = (eventType: string) => {
  switch (eventType) {
    case "call":
      return <Phone className="h-5 w-5 text-blue-500" />;
    case "email":
      return <Mail className="h-5 w-5 text-green-500" />;
    case "meeting":
      return <Calendar className="h-5 w-5 text-purple-500" />;
    case "note":
      return <Pencil className="h-5 w-5 text-yellow-500" />;
    case "task":
      return <CheckSquare className="h-5 w-5 text-red-500" />;
    default:
      return <MessageSquare className="h-5 w-5 text-gray-500" />;
  }
};

export const EventTimeline = ({ events = [], refreshKey, leadId }: EventTimelineProps) => {
  const [sortedEvents, setSortedEvents] = useState<any[]>([]);
  const [localEvents, setLocalEvents] = useState<any[]>(events);

  // Set up real-time subscription for lead events
  useEffect(() => {
    if (!leadId) {
      setSortedEvents([...events]);
      return;
    }

    // Initialize with the provided events
    setLocalEvents(events);

    // Create a channel for lead_events table changes for this specific lead
    const eventsChannel = supabase
      .channel(`lead_events_${leadId}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'lead_events',
        filter: `lead_id=eq.${leadId}`
      }, (payload) => {
        console.log('Real-time event update received:', payload);
        
        // Handle different event types
        if (payload.eventType === 'INSERT') {
          setLocalEvents(prevEvents => [payload.new, ...prevEvents]);
        } else if (payload.eventType === 'UPDATE') {
          setLocalEvents(prevEvents => 
            prevEvents.map(event => 
              event.id === payload.new.id ? payload.new : event
            )
          );
        } else if (payload.eventType === 'DELETE') {
          setLocalEvents(prevEvents => 
            prevEvents.filter(event => event.id !== payload.old.id)
          );
        }
      })
      .subscribe();

    // Cleanup subscription on unmount
    return () => {
      supabase.removeChannel(eventsChannel);
    };
  }, [events, leadId]);

  // Sort events whenever localEvents or refreshKey changes
  useEffect(() => {
    // Sort events by created_at in descending order
    const sorted = [...localEvents].sort((a, b) => 
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
    setSortedEvents(sorted);
  }, [localEvents, refreshKey]);

  if (sortedEvents.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No events recorded yet
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {sortedEvents.map((event, index) => (
        <EventItem 
          key={event.id || index} 
          event={event} 
        />
      ))}
    </div>
  );
};

const EventItem = ({ event }: { event: any }) => {
  const { toast } = useToast();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  const { data: createdBy } = useQuery({
    queryKey: ['user', event.created_by],
    queryFn: async () => {
      if (!event.created_by) return null;
      const { data, error } = await supabase
        .from('profiles')
        .select('full_name, email')
        .eq('id', event.created_by)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!event.created_by
  });

  const formatEventTitle = (eventType: string) => {
    return eventType
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const handleDeleteEvent = async () => {
    try {
      setIsDeleting(true);
      
      // Use the Supabase client's functions.invoke method instead of direct fetch
      const { data, error } = await supabase.functions.invoke('delete-lead-event', {
        body: { eventId: event.id }
      });
      
      if (error) {
        throw new Error(error.message || 'Failed to delete event');
      }
      
      toast({
        title: "Event deleted",
        description: "The event has been successfully deleted"
      });
      
      // The real-time subscription will update the UI
    } catch (error) {
      console.error("Error deleting event:", error);
      toast({
        title: "Error",
        description: "Failed to delete event. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const eventIcon = getEventIcon(event.event_type);

  return (
    <div className="relative pl-6 border-l py-4 group">
      <div className="absolute left-[-8px] top-6 bg-background rounded-full p-1 border">
        {eventIcon}
      </div>

      <div className="flex justify-between items-start">
        <div className="flex-1">
          <Badge variant="outline" className="mb-2">
            {formatEventTitle(event.event_type)}
          </Badge>
          <p className="whitespace-pre-wrap text-sm">{event.description}</p>
        </div>
        <Button 
          variant="ghost" 
          size="icon" 
          className="opacity-0 group-hover:opacity-100 transition-opacity" 
          onClick={() => setShowDeleteDialog(true)}
          aria-label="Delete event"
          disabled={isDeleting}
        >
          <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
        </Button>
      </div>

      <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
        <Clock className="h-3 w-3" />
        <span>{format(new Date(event.created_at), "MMM d, yyyy 'at' h:mm a")}</span>
        {createdBy && (
          <>
            <span>•</span>
            <User className="h-3 w-3" />
            <span>{createdBy.full_name || createdBy.email}</span>
          </>
        )}
      </div>

      <ConfirmDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        onConfirm={handleDeleteEvent}
        title="Delete Event"
        description="Are you sure you want to delete this event? This action cannot be undone."
      />
    </div>
  );
};
